import { AgreementApiModel } from '../models/DealModel';
import { User } from '../models/UserModels';
import checkExpiryStatus from './checkExpiryStatus';
import { dealHasExpired } from './dataHelpers';
import { getSearchTokens, matchesSearchTokens } from './searchNormalization';

export const AGREEMENT_FILTER_ZONE_VALUES = ['a', 'b', 'c', 'br', 'in', 'us'];

interface AgreementFilterContext {
  agreements: AgreementApiModel[];
  activeTab?: number;
  action?: string;
  people?: User[];
}

interface FilterAgreementsParams extends AgreementFilterContext {
  searchText?: string;
  selectedZones?: string[];
  selectedStatus?: string[];
}

export function getBaseFilteredAgreements({
  agreements,
  activeTab,
  action,
  people,
}: AgreementFilterContext): AgreementApiModel[] {
  let filteredDeals = agreements;

  if (typeof activeTab === 'number') {
    filteredDeals = filteredDeals.filter((deal) =>
      activeTab === 0 ? !dealHasExpired(deal) : dealHasExpired(deal),
    );
  }

  if (action === 'remove' && people && people.length > 0) {
    filteredDeals = filteredDeals.filter((deal) =>
      people.every((user) =>
        user.deals.some((userDeal) => userDeal.dealId === deal.id),
      ),
    );
  }

  return filteredDeals;
}

export function filterAgreements({
  agreements,
  activeTab,
  action,
  people,
  searchText,
  selectedZones = [],
  selectedStatus = [],
}: FilterAgreementsParams): AgreementApiModel[] {
  const searchTokens = getSearchTokens(searchText);
  const normalizedZones = selectedZones.map((zone) => zone.toLowerCase());
  const normalizedStatuses = selectedStatus.map((status) =>
    status.toLowerCase(),
  );

  let filteredDeals = getBaseFilteredAgreements({
    agreements,
    activeTab,
    action,
    people,
  });

  if (searchTokens.length > 0) {
    filteredDeals = filteredDeals.filter(({ dealName, id, products }) => {
      if (
        matchesSearchTokens(dealName, searchTokens) ||
        matchesSearchTokens(id, searchTokens)
      ) {
        return true;
      }

      // Check products under this agreement
      if (products && products.length > 0) {
        return products.some(
          (product) =>
            matchesSearchTokens(product.sku, searchTokens) ||
            matchesSearchTokens(product.name, searchTokens),
        );
      }

      return false;
    });
  }

  // When "allZones" is explicitly selected, show only deals with no zone restrictions
  if (selectedZones.includes('allZones')) {
    filteredDeals = filteredDeals.filter(
      (deal) => !deal.zones || deal.zones.length === 0,
    );
  } else if (normalizedZones.length > 0) {
    filteredDeals = filteredDeals.filter((deal) => {
      if (!deal.zones || deal.zones.length === 0) {
        return true;
      }

      return normalizedZones.every((selectedZone) =>
        deal.zones.some((dealZone) => dealZone.toLowerCase() === selectedZone),
      );
    });
  }

  if (normalizedStatuses.length > 0) {
    filteredDeals = filteredDeals.filter((deal) => {
      const isExpired = dealHasExpired(deal);
      const isDisabled = !deal.enabled;
      const { isExpiring } = checkExpiryStatus(deal.expiration);

      return normalizedStatuses.some((status) => {
        if (status === 'active' && !isExpired) return true;
        if (status === 'expired' && isExpired) return true;
        if (status === 'disabled' && isDisabled) return true;
        if (status === 'expiring' && isExpiring) return true;
        return false;
      });
    });
  }

  return filteredDeals;
}

export function hasExpiringAgreements(
  agreements: AgreementApiModel[],
): boolean {
  return agreements.some(
    (deal) => checkExpiryStatus(deal.expiration).isExpiring,
  );
}

export function hasDisabledAgreements(
  agreements: AgreementApiModel[],
): boolean {
  return agreements.some((deal) => !deal.enabled);
}

export function getVisibleZoneValues(
  agreements: AgreementApiModel[],
  allZoneValues: string[] = AGREEMENT_FILTER_ZONE_VALUES,
): string[] {
  const hasAllZonesDeal = agreements.some(
    (deal) => !deal.zones || deal.zones.length === 0,
  );

  if (hasAllZonesDeal) {
    return allZoneValues;
  }

  const availableZoneSet = new Set<string>();

  agreements.forEach((deal) => {
    deal.zones?.forEach((zone) => {
      availableZoneSet.add(zone.toLowerCase());
    });
  });

  return allZoneValues.filter((zone) => availableZoneSet.has(zone));
}
